import React, { useEffect, useState } from "react";
import { Badge, Card, Dropdown, Modal, Nav, Tab, Table } from "react-bootstrap";
import PageTitle from "../layouts/PageTitle";
import Spinner from "../components/Common/Spinner";
import Pagination from "../components/Common/Pagination";
import ConfirmModalLock from "../components/Modal/ConfirmModalLock";
import { notifyError, notifyTopRight } from "../components/Common/Toaster";
import {
  getVipDescriptionList,
  updateVipDescriptionStatus,
} from "../../services/UserServices/UserService";
import { useLocation, useNavigate } from "react-router-dom";

const limit = 10;

const REJECTION_REASON_OPTIONS = [
  "Your Signature Bio cannot include contact details, social handles, usernames, or references to other platforms. Please keep conversations and connections on MatchCatch.",
  "Your Signature Bio cannot include payment, arrangement, exchange, allowance, or other transactional wording. Please revise it in a more refined and non-transactional way.",
  "Your Signature Bio cannot include explicit, suggestive, or disrespectful wording. Please revise it to keep the tone respectful, polished, and appropriate.",
  "Your Signature Bio includes wording that does not meet our guidelines. Please remove any contact details, off-app references, transactional wording, explicit language, or unsuitable claims before resubmitting.",
  "Your Signature Bio does not currently meet our profile standards. Please revise it with a short, respectful note about your style, values, or the kind of connection you appreciate.",
];

const TAB_OPTIONS = [
  { key: "pending", label: "Pending" },
  { key: "accepted", label: "Accepted" },
  { key: "rejected", label: "Rejected" },
];

const svg1 = (
  <svg width="20px" height="20px" viewBox="0 0 24 24" version="1.1">
    <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
      <rect x="0" y="0" width="24" height="24"></rect>
      <circle fill="#000000" cx="5" cy="12" r="2"></circle>
      <circle fill="#000000" cx="12" cy="12" r="2"></circle>
      <circle fill="#000000" cx="19" cy="12" r="2"></circle>
    </g>
  </svg>
);

const getDescriptionText = (value, fallback = "-") => {
  if (value == null || value === "") return fallback;
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    const text = value.description ?? value.text;
    if (text == null || text === "") return fallback;
    return String(text);
  }
  return String(value);
};

const getVipDescriptionData = (item) => {
  const vip = item?.vipDescription || {};
  const pending = vip?.pending;
  const current = vip?.current ?? vip?.accepted;
  const rejected = vip?.rejected;

  return {
    current: getDescriptionText(current),
    updated: getDescriptionText(pending?.description ?? pending),
    status: pending?.status || "Accepted",
    rejectionReason: getDescriptionText(
      pending?.rejectionReason ??
      rejected?.rejectionReason ??
      rejected?.reason,
      ""
    ),
    updatedAt: vip?.updatedAt,
  };
};

const normalizeStatus = (status) =>
  String(status || "pending").toLowerCase();

const StatusBadge = ({ status }) => {
  const normalized = normalizeStatus(status);
  if (normalized === "accepted") {
    return (
      <Badge bg="" className="badge-success">
        Accepted
      </Badge>
    );
  }
  if (normalized === "rejected") {
    return (
      <Badge bg="" className="badge-danger">
        Rejected
      </Badge>
    );
  }
  return (
    <Badge bg="" className="badge-warning">
      Pending
    </Badge>
  );
};

export default function VipDescriptionList() {
  const location = useLocation();
  const navigate = useNavigate();
  const [loader, setLoader] = useState(false);
  const [users, setUsers] = useState([]);
  const [listLength, setListLength] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(
    location?.state?.page ? location.state.page : 0
  );
  const [selectedTab, setSelectedTab] = useState(
    location?.state?.selectedTab || "pending"
  );
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedReason, setSelectedReason] = useState("");
  const [customRejectionReason, setCustomRejectionReason] = useState("");
  const [rejectionReasonError, setRejectionReasonError] = useState("");
  const [customRejectionReasonError, setCustomRejectionReasonError] =
    useState("");
  const [actionLoader, setActionLoader] = useState(false);

  const fetchList = async (page = currentPage, type = selectedTab) => {
    setLoader(true);
    try {
      const response = await getVipDescriptionList(page, limit, type);
      const payload = response.data?.data || {};
      const list = payload?.descriptions || payload?.users || [];
      const total = payload?.count ?? list.length;

      setUsers(list);
      setListLength(total);
      setPageCount(Math.ceil(total / limit));

      if (page > 0 && list.length === 0) {
        setCurrentPage(0);
      }
    } catch (error) {
      notifyError(error.response?.data?.message || "Failed to load list");
      console.log(error, "error");
    } finally {
      setLoader(false);
    }
  };

  const onApprove = async (userId) => {
    setActionLoader(true);
    try {
      const response = await updateVipDescriptionStatus({
        userId,
        status: "accepted",
        rejectionReason: "",
      });
      notifyTopRight(response.data?.message || "Status updated successfully");
      setShowApproveModal(false);
      setSelectedUserId("");
      fetchList();
    } catch (error) {
      notifyError(error.response?.data?.message || "Failed to update status");
      console.log(error.response, "error");
    } finally {
      setActionLoader(false);
    }
  };

  const onReject = async (e) => {
    e.preventDefault();

    if (!selectedReason) {
      setRejectionReasonError("Rejection reason is required");
      return;
    }

    if (selectedReason === "other") {
      const trimmedCustomReason = customRejectionReason.trim();
      if (!trimmedCustomReason) {
        setCustomRejectionReasonError("Rejection reason is required");
        return;
      }
    }

    const rejectionReason =
      selectedReason === "other"
        ? customRejectionReason.trim()
        : selectedReason;

    setActionLoader(true);
    try {
      const response = await updateVipDescriptionStatus({
        userId: selectedUserId,
        status: "rejected",
        rejectionReason,
      });
      notifyTopRight(response.data?.message || "Status updated successfully");
      setShowRejectModal(false);
      setSelectedUserId("");
      setSelectedReason("");
      setCustomRejectionReason("");
      setRejectionReasonError("");
      setCustomRejectionReasonError("");
      fetchList();
    } catch (error) {
      notifyError(error.response?.data?.message || "Failed to update status");
      console.log(error.response, "error");
    } finally {
      setActionLoader(false);
    }
  };

  useEffect(() => {
    fetchList(currentPage, selectedTab);
  }, [currentPage, selectedTab]);

  const handleTabSelect = (key) => {
    if (!key || key === selectedTab) return;
    setSelectedTab(key);
    setCurrentPage(0);
  };

  const openRejectModal = (userId) => {
    setSelectedUserId(userId);
    setSelectedReason("");
    setCustomRejectionReason("");
    setRejectionReasonError("");
    setCustomRejectionReasonError("");
    setShowRejectModal(true);
  };

  const openApproveModal = (userId) => {
    setSelectedUserId(userId);
    setShowApproveModal(true);
  };

  return (
    <div>
      <PageTitle
        activeMenu="VIP Description Requests"
        motherMenu="VIP Description"
      />
      <Card>
        <Card.Body className="pb-0 pt-3">
          <div className="default-tab mb-3">
            <Tab.Container
              activeKey={selectedTab}
              onSelect={handleTabSelect}
            >
              <Nav as="ul" className="nav-tabs">
                {TAB_OPTIONS.map((tab) => (
                  <Nav.Item as="li" key={tab.key}>
                    <Nav.Link eventKey={tab.key}>{tab.label}</Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>
            </Tab.Container>
          </div>
          {loader ? (
            <Spinner />
          ) : (
            <Table responsive className="mostly-customized-scrollbar mb-0">
              <thead style={{ color: "black" }}>
                <tr>
                  <th>
                    <strong>NAME</strong>
                  </th>
                  <th>
                    <strong>EMAIL</strong>
                  </th>
                  <th>
                    <strong>Phone Number</strong>
                  </th>
                  <th>
                    <strong>CURRENT DESCRIPTION</strong>
                  </th>
                  {selectedTab !== "accepted" && (<th>
                    <strong>UPDATED DESCRIPTION</strong>
                  </th>)}
                  <th>
                    <strong>STATUS</strong>
                  </th>
                  {selectedTab === "rejected" && (<th>
                    <strong>REJECTION REASON</strong>
                  </th>)}
                  {selectedTab === "pending" && <th>
                    <strong>ACTION</strong>
                  </th>}
                </tr>
              </thead>
              <tbody>
                {users?.map((item, i) => {
                  const vipData = getVipDescriptionData(item);
                  const userId = item?._id || item?.userId;

                  return (
                    <tr
                      key={userId || i}
                      style={{
                        fontSize: "14px",
                      }}
                    >
                      <td
                        className="pointer"
                        onClick={() =>
                          navigate("/member-details", {
                            state: { data: userId, page: currentPage },
                          })
                        }
                      >
                        {item?.name || "-"}
                      </td>
                      <td>{item?.email || "-"}</td>
                      <td>{item?.countryCode ? "+" + item?.countryCode + " " + item?.phoneNumber : "-"}</td>
                      <td style={{ maxWidth: "220px", whiteSpace: "pre-wrap" }}>
                        {vipData.current}
                      </td>
                      {selectedTab !== "accepted" && <td style={{ maxWidth: "220px", whiteSpace: "pre-wrap" }}>
                        {vipData.updated}
                      </td>}
                      <td>
                        <StatusBadge status={vipData.status} />
                      </td>
                      {selectedTab === "rejected" && <td style={{ maxWidth: "180px", whiteSpace: "pre-wrap" }}>
                        {vipData.rejectionReason || "-"}
                      </td>}
                      {selectedTab === "pending" && <td>
                        <Dropdown>
                          <Dropdown.Toggle
                            variant="info light"
                            className="light sharp btn btn-info i-false"
                            disabled={actionLoader}
                          >
                            {svg1}
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            {selectedTab === "pending" && (
                              <>
                                <Dropdown.Item
                                  onClick={() => openApproveModal(userId)}
                                >
                                  Accept
                                </Dropdown.Item>
                                <Dropdown.Item
                                  onClick={() => openRejectModal(userId)}
                                >
                                  Reject
                                </Dropdown.Item>
                              </>
                            )}
                            {selectedTab === "accepted" && (
                              <Dropdown.Item
                                onClick={() => openRejectModal(userId)}
                              >
                                Reject
                              </Dropdown.Item>
                            )}
                            {selectedTab === "rejected" && (
                              <Dropdown.Item
                                onClick={() => openApproveModal(userId)}
                              >
                                Accept
                              </Dropdown.Item>
                            )}
                          </Dropdown.Menu>
                        </Dropdown>
                      </td>}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
          {users?.length === 0 && !loader && (
            <div className="justify-content-center d-flex my-5">
              Sorry, Data Not Found!
            </div>
          )}
        </Card.Body>
        {users?.length !== 0 && (
          <div className="card-footer clearfix">
            <div className="d-block d-sm-flex justify-content-between align-items-center">
              <div className="dataTables_info">
                Showing {currentPage * limit + 1} to{" "}
                {listLength > (currentPage + 1) * limit
                  ? (currentPage + 1) * limit
                  : listLength}{" "}
                of {listLength} entries
              </div>
              <Pagination
                pageCount={pageCount}
                pageValue={currentPage}
                setPage={setCurrentPage}
              />
            </div>
          </div>
        )}
      </Card>

      <ConfirmModalLock
        open={showApproveModal}
        title="Approve VIP Description"
        description="Are you sure you want to approve this bio? Once approved, it cannot be rejected later."
        confirmText="Approve"
        onConfirm={() => onApprove(selectedUserId)}
        onCancel={() => {
          setShowApproveModal(false);
          setSelectedUserId("");
        }}
        loading={actionLoader}
      />

      <Modal
        show={showRejectModal}
        onHide={() => setShowRejectModal(false)}
        centered
      >
        <form onSubmit={onReject}>
          <div className="modal-header">
            <h4 className="modal-title fs-20">Rejection Reason</h4>
          </div>
          <div className="modal-body">
            <div className="form-group mb-3">
              <label className="text-black font-w500">
                Reason <span className="text-danger">*</span>
              </label>
              <select
                className="form-control"
                value={selectedReason}
                onChange={(e) => {
                  setSelectedReason(e.target.value);
                  setRejectionReasonError("");
                  setCustomRejectionReasonError("");
                  if (e.target.value !== "other") {
                    setCustomRejectionReason("");
                  }
                }}
              >
                <option value="">Select Reason</option>
                {REJECTION_REASON_OPTIONS.map((reason, index) => (
                  <option key={index} value={reason}>
                    {reason}
                  </option>
                ))}
                <option value="other">Other</option>
              </select>
              {rejectionReasonError && (
                <small className="text-danger">{rejectionReasonError}</small>
              )}
            </div>
            {selectedReason === "other" && (
              <div className="form-group mb-3">
                <label className="text-black font-w500">
                  Reason <span className="text-danger">*</span>
                </label>
                <textarea
                  rows={4}
                  className="form-control"
                  value={customRejectionReason}
                  onChange={(e) => {
                    setCustomRejectionReason(e.target.value);
                    setCustomRejectionReasonError("");
                  }}
                  placeholder="Enter rejection reason"
                />
                {customRejectionReasonError && (
                  <small className="text-danger">
                    {customRejectionReasonError}
                  </small>
                )}
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-danger light"
              onClick={() => setShowRejectModal(false)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={
                actionLoader ||
                !selectedReason ||
                (selectedReason === "other" && !customRejectionReason.trim())
              }
            >
              Reject
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
